import type { Theme } from '@mui/material/styles/createTheme'
import { css } from '@emotion/react'
import { styled } from '@mui/material'
import MuiChip from '@mui/material/Chip'
import type { ChipProps } from './Chip.types'

type CssProps = ChipProps & { theme?: Theme }

const roundStyles = ({ rounded }: Pick<CssProps, 'rounded'>) => {
  if (rounded)
    return css`
      border-radius: 16px;
      &:hover::before {
        border-radius: 16px;
      }
    `
  return css`
    border-radius: 0;
    &:hover::before {
      border-radius: 0;
    }
  `
}

const sizeStyles = ({ size }: Pick<CssProps, 'size'>) => {
  switch (size) {
    case 'xSmall':
      return css`
        height: 16px;
        .MuiChip-label {
          height: 16px;
          line-height: 16px;
          font-size: 10px;
        }
      `
    case 'small':
      return css`
        height: 24px;
        .MuiChip-label {
          height: 24px;
          line-height: 24px;
          font-size: 12px;
        }
      `
    case 'large':
      return css`
        height: 54px;
        .MuiChip-label {
          height: 54px;
          line-height: 54px;
          font-size: 16px;
        }
      `
    case 'xLarge':
      return css`
        height: 66px;
        .MuiChip-label {
          height: 66px;
          line-height: 66px;
          font-size: 18px;
        }
      `
    case 'medium':
    default:
      return css`
        height: 32px;
        .MuiChip-label {
          height: 32px;
          line-height: 32px;
          font-size: 14px;
        }
      `
  }
}

const variantStyles = ({
  color,
  variant,
  theme,
}: Pick<CssProps, 'color' | 'variant' | 'theme'>) => {
  switch (variant) {
    case 'text':
      if (!color) {
        return css`
          background-color: ${theme?.palette.common.gray[300]};
          color: rgba(0, 0, 0, 87%);
        `
      }
      return css`
        color: ${theme?.palette[color].main};
        background-color: ${theme?.palette[color].refer?.[50]};
        &.MuiChip-clickable {
          &:hover {
            background-color: ${theme?.palette[color].refer?.[100]};
          }
        }
      `
    case 'outlined':
      return css`
        position: relative;
        background-color: ${theme?.palette.common.white};
        &.MuiChip-clickable {
          ::before {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            content: '';
            color: inherit;
            background-color: currentcolor;
            opacity: 0;
          }
          &:hover {
            background-color: ${theme?.palette.common.white};
            ::before {
              opacity: 0.04;
            }
          }
        }
      `
    case 'filled':
      if (!color) {
        return css`
          background-color: ${theme?.palette.common.gray[500]};
          color: #fff;
        `
      }
      return null
    default:
      return null
  }
}

const commonStyles = () => css`
  .MuiChip-label {
    padding: 0 8px;
  }
  .MuiChip-deleteIcon {
    color: inherit;
    font-size: 18px;
    height: 18px;
    width: 18px;
    &:hover {
      color: inherit;
      opacity: 0.72;
    }
  }
`
export const Chip = styled(MuiChip, {
  shouldForwardProp: (prop) => prop !== 'rounded',
})<ChipProps>`
  ${commonStyles};
  ${roundStyles};
  ${sizeStyles};
  ${variantStyles};
  ${roundStyles}
`
